/**
 * OutcomeBadge Component
 *
 * Semantic badge for FOS (Financial Ombudsman Service) outcome display.
 * Uses GO brand colors with regulatory meaning:
 * - Berry (#92204D) → Upheld (risk to firm - complaint was justified)
 * - Teal (#07A0C3) → Not Upheld (favorable - firm was right)
 * - Purple (#924EFF) → Partially Upheld (mixed outcome - needs review)
 *
 * Reusable across:
 * - FOS Explorer search results
 * - Signal detail "Similar Decisions"
 * - Cluster detail panels
 * - Any ombudsman decision context
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const outcomeBadgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full font-medium transition-colors",
  {
    variants: {
      outcome: {
        upheld: "bg-berry/10 text-berry border border-berry/20",
        not_upheld: "bg-teal/10 text-teal border border-teal/20",
        partially_upheld: "bg-purple/10 text-purple border border-purple/20",
      },
      size: {
        sm: "text-xs px-2 py-0.5",
        md: "text-sm px-2.5 py-1",
        lg: "text-base px-3 py-1.5",
      },
    },
    defaultVariants: {
      outcome: "not_upheld",
      size: "md",
    },
  }
);

const OUTCOME_LABELS: Record<string, string> = {
  upheld: "Upheld",
  not_upheld: "Not Upheld",
  partially_upheld: "Partial",
};

const OUTCOME_DOT_COLORS: Record<string, string> = {
  upheld: "bg-berry",
  not_upheld: "bg-teal",
  partially_upheld: "bg-purple",
};

export type OutcomeType = "upheld" | "not_upheld" | "partially_upheld";

export interface OutcomeBadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'>,
    VariantProps<typeof outcomeBadgeVariants> {
  /** The FOS outcome type */
  outcome: OutcomeType;
  /** Whether to show the colored indicator dot */
  showDot?: boolean;
  /** Whether to show the outcome label text */
  showLabel?: boolean;
}

/**
 * Normalizes various outcome string formats to our canonical types
 */
export function normalizeOutcome(outcome: string | null | undefined): OutcomeType {
  if (!outcome) return "not_upheld";
  const lower = outcome.toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
  if (lower === "upheld") return "upheld";
  if (lower === "not_upheld" || lower === "notpheld") return "not_upheld";
  if (lower.includes("partial")) return "partially_upheld";
  return "not_upheld";
}

const OutcomeBadge = React.forwardRef<HTMLSpanElement, OutcomeBadgeProps>(
  (
    {
      className,
      outcome,
      size,
      showDot = true,
      showLabel = true,
      ...props
    },
    ref
  ) => {
    const label = OUTCOME_LABELS[outcome] || outcome;
    const dotColor = OUTCOME_DOT_COLORS[outcome] || "bg-muted-foreground";

    return (
      <span
        ref={ref}
        className={cn(outcomeBadgeVariants({ outcome, size }), className)}
        {...props}
      >
        {showDot && (
          <span
            className={cn(
              "rounded-full flex-shrink-0",
              dotColor,
              size === "sm" ? "w-1.5 h-1.5" : size === "lg" ? "w-2.5 h-2.5" : "w-2 h-2"
            )}
            aria-hidden="true"
          />
        )}
        {showLabel && <span>{label}</span>}
      </span>
    );
  }
);
OutcomeBadge.displayName = "OutcomeBadge";

/**
 * Color constants for use in charts and visualizations
 * These match the Tailwind colors defined in tailwind.config.js
 */
export const OUTCOME_COLORS = {
  upheld: "#92204D", // berry - risk to firm
  not_upheld: "#07A0C3", // teal - favorable
  partially_upheld: "#924EFF", // purple - mixed
  unknown: "#6B7280", // gray
} as const;

export { OutcomeBadge, outcomeBadgeVariants };
